import { useMemo, useEffect, useReducer, useCallback } from 'react';
import axios from 'src/utils/axios';
import { endpoints } from 'src/utils/api-endpoints';
import { AuthContext } from './auth-context';
import { getLatestAccessToken, setLocalStorage } from './utils';
import { AuthUserType, ActionMapType, AuthStateType, JWTContextType } from '../../types';
import { User } from 'src/models/user';
import { updateUser as apiUpdateUser } from 'src/api/user';
import { accessTokenRef } from './access-token-ref';

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  UPDATE = 'UPDATE',
  LOGOUT = 'LOGOUT',
  FORGOT_PASSWORD_EMAIL = 'FORGOT_PASSWORD_EMAIL',
}

type Payload = {
  [Types.INITIAL]: { user: AuthUserType };
  [Types.LOGIN]: { user: AuthUserType };
  [Types.REGISTER]: { isRegistered: boolean };
  [Types.UPDATE]: { user: AuthUserType };
  [Types.LOGOUT]: undefined;
  [Types.FORGOT_PASSWORD_EMAIL]: { passwordResetToken: string };
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

const initialState: AuthStateType = {
  user: null,
  loading: true,
  isRegistered: false,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return { loading: false, user: action.payload.user };
  }
  if (action.type === Types.LOGIN) {
    return { ...state, user: action.payload.user };
  }
  if (action.type === Types.REGISTER) {
    return { ...state, isRegistered: action.payload.isRegistered };
  }
  if (action.type === Types.UPDATE) {
    return { ...state, user: action.payload.user };
  }
  if (action.type === Types.LOGOUT) {
    return { ...state, user: null, loading: false };
  }
  if (action.type === Types.FORGOT_PASSWORD_EMAIL) {
    return { ...state, accessToken: action.payload.passwordResetToken };
  }
  return state;
};

type Props = { children: React.ReactNode };

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // ✅ CHANGE 1 — initialize always fetches fresh user from API, never reads localStorage
  const initialize = useCallback(async () => {
    try {
      const newToken = await getLatestAccessToken();

      if (newToken) {
        accessTokenRef.current = newToken;

        // Always get fresh user data from API
        const res = await axios.get(endpoints.auth.me);
        const freshUser = res.data?.data ?? res.data;

        dispatch({
          type: Types.INITIAL,
          payload: { user: { ...freshUser, accessToken: newToken } },
        });
      } else {
        accessTokenRef.current = null;
        dispatch({ type: Types.INITIAL, payload: { user: null } });
      }
    } catch {
      accessTokenRef.current = null;
      dispatch({ type: Types.INITIAL, payload: { user: null } });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

 useEffect(() => {
  if (!state.user) {
    return undefined; // ✅ consistent return
  }

  const interval = setInterval(async () => {
    try {
      const res = await axios.get(endpoints.auth.me);
      const freshUser = res.data?.data ?? res.data;

      const currentRoles = JSON.stringify(state.user?.jobRoles ?? []);
      const freshRoles = JSON.stringify(freshUser?.jobRoles ?? []);

      if (currentRoles !== freshRoles) {
        dispatch({
          type: Types.UPDATE,
          payload: {
            user: { ...state.user, ...freshUser, accessToken: accessTokenRef.current },
          },
        });
      }
    } catch {
      // silent fail
    }
  }, 60000);

  return () => clearInterval(interval); // ✅ consistent return
}, [state.user]);

  // ✅ CHANGE 3 — login no longer calls setLocalStorage for user data
  const login = useCallback(async (email: string, password: string, rememberMe: boolean) => {
    const res = await axios.post(endpoints.auth.login, { email, password, rememberMe });
    const { accessToken, user } = res.data;

    accessTokenRef.current = accessToken; // memory only
    // ❌ removed setLocalStorage(user) — user data never touches localStorage

    dispatch({
      type: Types.LOGIN,
      payload: { user: { ...user, accessToken } },
    });
  }, []);

  // REGISTER — unchanged
  const register = useCallback(
    async (
      email: string,
      password: string,
      firstName: string,
      lastName: string,
      organizationName?: string
    ) => {
      const res = await axios.post(endpoints.auth.register, {
        email,
        password,
        firstName,
        lastName,
        organizationName,
      });

      const { accessToken } = res.data;
      accessTokenRef.current = accessToken;

      dispatch({
        type: Types.REGISTER,
        payload: { isRegistered: true },
      });
    },
    []
  );

  // UPDATE USER — unchanged
  const updateUser = useCallback(
    async (data: Partial<User> & { _id: string }) => {
      const res = await apiUpdateUser(data);
      if (res.success) {
        const updatedUser = res.data;

        if (state.user?._id === updatedUser._id) {
          dispatch({
            type: Types.UPDATE,
            payload: { user: { ...updatedUser } },
          });
        }

        return updatedUser;
      } else {
        throw new Error('Failed to update user');
      }
    },
    [state.user]
  );

  // LOGOUT — unchanged
  const logout = useCallback(async () => {
    const res = await axios.post(endpoints.auth.logout);
    if (res.status === 200) {
      accessTokenRef.current = null;
      setLocalStorage(null); // cleans up any old localStorage data
      dispatch({ type: Types.LOGOUT });
    }
  }, []);

  // FORGOT PASSWORD — unchanged
  const sendPasswordResetLink = useCallback(async (email: string) => {
    const res = await axios.post(endpoints.auth.sendForgotPasswordEmail, { email });
    const { accessToken } = res.data;

    dispatch({
      type: Types.FORGOT_PASSWORD_EMAIL,
      payload: { passwordResetToken: accessToken },
    });
  }, []);

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';
  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue: JWTContextType = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      isRegistered: state.isRegistered ?? false,
      login,
      register,
      logout,
      updateUser,
      sendPasswordResetLink,
    }),
    [state.user, state.isRegistered, status, login, register, logout, updateUser, sendPasswordResetLink]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}